import { tzOffset } from '../../helpers/date-time';
import { buildParams } from '../../helpers/path';
import { PaginationRequest, PaginationType } from '../../types/pagination';
import {
  CustomCacheRequest,
  InfiniteScrollRequest,
  LanguageRequest,
  SortRequest,
} from '../../types/props';
import { managerApiUrl } from './manager-api';
import { TaskTypeType } from './tasks-api';
import { baseFetch } from './utils.ts/base-fetch';
import { downloadFile, fileFetch } from './utils.ts/file-fetch';

export type ReviewType = {
  id: number;
  place_id: number;
  lead_id: string | null;
  user_id: string | null;
  full_name: string;
  contact_info: string;
  content: string;
  rating: number;
  created_at: string;
  task_id: number;
  task_type: string | null;
  qr: {
    id: string;
    value: string;
  };
};

export type FetchReviewsRequest = {
  rating?: string;
  qr?: string;
  is_by_company?: boolean;
  type?: TaskTypeType;
  from?: string;
  to?: string;
} & Partial<Omit<ReviewType, 'rating' | 'qr'>> &
  SortRequest &
  CustomCacheRequest &
  PaginationRequest &
  InfiniteScrollRequest &
  LanguageRequest;

export type ReviewCategoryType = {
  count: number;
  items: ReviewType[];
};

export const fetchReviews = async (
  params?: FetchReviewsRequest,
): Promise<
  PaginationType & {
    reviews: ReviewType[];
    positive: ReviewCategoryType;
    negative: ReviewCategoryType;
    neutral: ReviewCategoryType;
  }
> => {
  let url = `${managerApiUrl}/review`;

  const { customCache, ...other } = params || {};
  if (other) {
    url += buildParams({ ...other, offset: -tzOffset });
  }

  const response = await baseFetch(url, {
    customCache,
  });

  return response;
};

export type ReviewsExportFormatType = 'pdf' | 'excel';

export type ReviewsExportRequest = FetchReviewsRequest & {
  format: ReviewsExportFormatType;
};

export const fetchReviewsExport = async (params: ReviewsExportRequest) => {
  const { blob, fileName } = await fileFetch(
    `${managerApiUrl}/review/export${buildParams({ ...params, offset: -tzOffset })}`,
  );

  const defaultFileNames = {
    excel: 'export.xlsx',
    pdf: 'export.pdf',
  };

  downloadFile(blob, fileName || defaultFileNames[params.format]);
};

export type CreateReviewRequest = {
  place_id: number;
  content: string;
  qr_id: string;
  rating?: number | null;
  task_id: number;
};

export const createReview = async (params: CreateReviewRequest) => {
  const response = await baseFetch('/public/review', {
    method: 'POST',
    body: JSON.stringify(params),
  });

  return response;
};
